/* eslint-disable  @typescript-eslint/no-explicit-any */
import { AnyAction, AsyncThunk, createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import {
  FulfillmentApi,
  FulfillmentTypeCollectionResponse,
  FulfillmentTypeCollection,
  getStoresByFulfillmentTypeProps,
  WarehouseCollection,
  FulfillmentCartPickupCollectionResponse,
  CartPickupLocationResponse,
  FulfillmentTypeIdReqPayloadType,
} from '@commerce-fulfillment-webcomponents/sdk';
import { cmsApi, WebEvents, WebEventsEnum } from '@commerce-webcomponents/sdk';
import { AppDispatch, SessionService, StorageService } from '@nextgen-web-framework/core';
import { PaymentMethodId } from '@commerce-payment-webcomponents/sdk';
import { checkoutApi } from '../requests';
import {
  InitialCheckoutState,
  CheckoutIdReqPayloadType,
  FulfillmentIdReqPayloadType,
  PincodeReqPayloadType,
  OrderIdReqPayloadType,
  createCheckoutProps,
  PlaceOrderProps,
  UpdatePaymentProps,
  CreatePaymentProps,
  CheckoutApiResponse,
  CheckoutVersionPayloadType,
  confirmDeliveryOptionProps,
  updateDeliveryOptionProps,
  IFulfillmentCmsResponse,
  ICheckoutCmsResponse,
  CheckoutTaxResponse,
  updateCheckoutProps,
  checkoutFulfillmentOptions,
  isPaginationType,
} from '../../types/checkoutTypes';
import { CheckoutStep } from '../../types/enums/checkoutEnums';
import { CheckoutResponse, CheckoutStatus, PaymentMethod, TaxDocumentResponse } from '../models';
import { transformCheckoutToCheckoutProfile } from '../../transformers/';
import { PaymentMethodType } from '../models/PaymentMethodType';
import {
  checkoutApiGenerics,
  checkoutCmsUid,
  checkoutCmsVersion,
  initialLoading,
  LocalStorageKeys,
} from '../config/checkoutApiConstants';
import {
  transformFulfillmentCmsData,
  transformFulfillmentLocations,
  transformFulfillmentTypesData,
  resetThirdPartyStoreList,
  getSelectedFulfillmentLocationStoreMap,
  transformCheckoutCmsData,
  getCountrySpecificMasterConfig,
} from '../../transformers/checkoutEventsTransformer';
import { CheckoutProfileService } from './../CheckoutProfile/CheckoutProfileService';

const HYDRATE = '__NEXT_REDUX_WRAPPER_HYDRATE__';

type CheckoutExtraType = {
  // Optional fields for defining thunkApi field types
  dispatch: AppDispatch; // this is store.dispatch
  state: InitialCheckoutState; // this is
  extra: {
    BASE_API_URL: string;
    ORDER_BASE_API_URL: string;
    CHECKOUT_BASE_API_URL: string;
  };
};

type GenericAsyncThunk = AsyncThunk<CheckoutApiResponse, unknown, CheckoutExtraType>;
type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

function isPendingGenericAction(action: AnyAction): action is PendingAction {
  return action.type.startsWith('commerceCheckout') && action.type.endsWith('/pending');
}
function isFulfilledAction(action: AnyAction): action is FulfilledAction {
  return action.type.startsWith('commerceCheckout') && action.type.endsWith('/fulfilled');
}

function isRejectedAction(action: AnyAction): action is RejectedAction {
  return action.type.startsWith('commerceCheckout') && action.type.endsWith('/rejected');
}

const setCheckoutProfile = async (data: CheckoutResponse) => {
  const localStorage = StorageService.getLocalStorage();
  let defaultWarehouseId = '';
  if (!data?.fulfillment?.warehouseId && data?.fulfillment?.id != checkoutFulfillmentOptions.BOPIS) {
    const postalCode =
      data?.fulfillment?.id == checkoutFulfillmentOptions.HOMEDELIVERY
        ? data?.fulfillment?.address?.postalCode
        : data?.fulfillment?.serviceLocation?.address?.postCode;
    defaultWarehouseId = await getDefaultWarehouseId(postalCode);
  }
  !SessionService.getSession().isAnonymous() &&
    CheckoutProfileService.updateFulfillmentType(transformCheckoutToCheckoutProfile(data, defaultWarehouseId));
  localStorage.store(LocalStorageKeys.WAREHOUSE_ID, data?.fulfillment?.warehouseId || defaultWarehouseId || '');
  WebEvents.publishEvent({
    event: WebEventsEnum.UpdateCheckoutProfileAddress,
    payload: {},
    sync: false,
  });
};

export const getDefaultWarehouseId = async (postalCode?: string) => {
  const baseUrl = ApiConfigService.getBaseUrl();
  const storeId = getCountrySpecificMasterConfig()?.warehouse?.id;
  const params: { baseUrl: string; postCode?: string } = { baseUrl };
  if (postalCode) {
    params.postCode = postalCode;
  }
  const response = (await FulfillmentApi.getWarehousesByPostCode(params)) as WarehouseCollection;
  const warehouseId = response?.warehouses?.[0]?.id ?? storeId;
  return warehouseId || '';
};

const setCheckoutPaymentProfile = (data: CheckoutResponse) => {
  if (!SessionService.getSession().isAnonymous() && data?.status != CheckoutStatus.OrderCreated) {
    const paymentMethodCodeEntry: PaymentMethod | undefined = data?.selectedPaymentMethods?.entries?.find(
      (entry) => entry.methodType == PaymentMethodType.External,
    );
    paymentMethodCodeEntry?.paymentMethodCode &&
      paymentMethodCodeEntry.paymentMethodCode != PaymentMethodId.InstallmentPayment &&
      CheckoutProfileService.updatePayment(paymentMethodCodeEntry.paymentMethodCode);
  }
};

const getRequestName = (requestParam: string) => {
  const [, reqName] = requestParam.split('/');
  return initialLoading[reqName] || reqName;
};

export const getCheckoutSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  CheckoutIdReqPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/getCheckout', async ({ checkoutId }) => {
  const res: CheckoutApiResponse = await checkoutApi.getCheckoutDetails({
    checkoutId,
  });

  return res;
});

// TODO: To be used in guest flow
// export const getCheckoutCartSliceReq = createAsyncThunk<
//   CartAPIResponse,
//   CartIdReqPayloadType,
//   {
//     dispatch: AppDispatch;
//     state: InitialCheckoutState;
//   }
// >('commerceCheckout/getCart', async ({ cartId }) => {
//   const res: CartAPIResponse = await cartApi.getCart({
//     cartId,
//   });

//   return res;
// });

export const createCheckoutSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  createCheckoutProps,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/createCheckout', async ({ payload }) => {
  const res: CheckoutApiResponse = await checkoutApi.createCheckout({
    payload,
  });

  return res;
});

export const updateCheckoutSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  CheckoutIdReqPayloadType & updateCheckoutProps,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/updateCheckout', async ({ checkoutId, payload }) => {
  const res: CheckoutApiResponse = await checkoutApi.updateCheckout({
    checkoutId,
    payload,
  });

  return res;
});

export const placeOrderCheckoutSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  PlaceOrderProps & CheckoutVersionPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/placeOrder', async ({ payload }) => {
  const res: CheckoutApiResponse = await checkoutApi.placeOrder({
    payload,
  });

  return res;
});

export const createPaymentCheckoutSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  CreatePaymentProps & CheckoutIdReqPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/createPaymentMethod', async ({ payload, checkoutId }) => {
  const res: CheckoutApiResponse = await checkoutApi.createPaymentMethod({
    checkoutId,
    payload,
  });

  return res;
});

export const updatePaymentCheckoutSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  UpdatePaymentProps & CheckoutIdReqPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/updatePaymentMethod', async ({ payload, checkoutId, paymentId }) => {
  const res: CheckoutApiResponse = await checkoutApi.updatePaymentMethod({
    checkoutId,
    payload,
    paymentId,
  });

  return res;
});

export const confirmDeliveryOptionCheckoutSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  confirmDeliveryOptionProps & CheckoutIdReqPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/confirmDeliveryOption', async ({ payload, checkoutId }) => {
  const res: CheckoutApiResponse = await checkoutApi.confirmDeliveryOption({
    checkoutId,
    payload,
  });

  return res;
});

export const updateDeliveryOptionCheckoutSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  updateDeliveryOptionProps & CheckoutIdReqPayloadType & FulfillmentIdReqPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/updateDeliveryOption', async ({ payload, checkoutId, fulfillmentId }) => {
  const res: CheckoutApiResponse = await checkoutApi.updateDeliveryOption({
    fulfillmentId,
    checkoutId,
    payload,
  });

  return res;
});

export const getFulfillmentCmsDataSliceReq = createAsyncThunk(
  'commerceFulfillment/allocation/getCmsContent',
  async () => {
    const res = await FulfillmentApi.getFulfillmentContentData();
    return res;
  },
);

export const getTncCmsDataSliceReq = createAsyncThunk('commerceCheckout/tnc/getCmsContent', async () => {
  const res = await cmsApi.fetchContent({
    uId: checkoutCmsUid,
    version: checkoutCmsVersion,
  });
  return res;
});

export const getCheckoutByOrderIdSliceReq = createAsyncThunk<
  CheckoutApiResponse,
  OrderIdReqPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/getCheckoutByOrderId', async ({ orderId }) => {
  const res: CheckoutApiResponse = await checkoutApi.getCheckoutByOrderId({
    orderId,
  });

  return res;
});

export const getTaxDocumentsSliceReq = createAsyncThunk<
  CheckoutTaxResponse,
  CheckoutIdReqPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
  }
>('commerceCheckout/getTaxDocuments', async ({ checkoutId }, _) => {
  const res: CheckoutTaxResponse = await checkoutApi.getTaxDocuments({
    checkoutId,
  });
  return res;
});

export const getFulfillmentTypesSliceReq = createAsyncThunk<
  FulfillmentTypeCollectionResponse,
  FulfillmentTypeIdReqPayloadType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
    extra: {
      BASE_API_URL: string;
    };
  }
>('commerceFulfillment/getFulfillmentTypes', async ({ params }, { extra }) => {
  const res: FulfillmentTypeCollectionResponse = await FulfillmentApi.getFulfillmentTypes({
    baseUrl: extra.BASE_API_URL,
    params,
  });
  return res;
});

export const getStoreLocationsSliceReq = createAsyncThunk<
  FulfillmentCartPickupCollectionResponse,
  getStoresByFulfillmentTypeProps & PincodeReqPayloadType & isPaginationType,
  {
    dispatch: AppDispatch;
    state: InitialCheckoutState;
    extra: {
      BASE_API_URL: string;
    };
  }
>('commerceFulfillment/getStoreLocations', async ({ payload }, { extra }) => {
  const res: FulfillmentTypeCollectionResponse = await FulfillmentApi.getStoresByFulfillmentType({
    baseUrl: extra.BASE_API_URL,
    payload,
  });
  return res;
});

const initialState: InitialCheckoutState = {
  checkout: null,
  cart: null,
  isLoading: false,
  hasError: false,
  disabledPlaceOrder: false,
  placeOrderEnablerStack: [],
  error: {},
  currentStep: CheckoutStep.Contact,
  pickupDrawerVisibility: false,
  pickupContactDrawerVisibility: false,
  deliveryOptionsVisibility: true,
  notificationPopupVisibility: false,
  eventsModalVisibility: false,
  loadingState: {},
  fulfillmentCmsContent: {},
  cmsContent: { tnc: [{ title: '', description: '', ordertype: '', accounttype: '' }] },
  deliveryOptionsContainerState: undefined,
  shippingDetailsContainerState: undefined,
  taxContactContainerState: undefined,
  fulfillmentTypesAndStoreMap: {
    delivery: { isExist: true, isRestricted: false },
    pickUp: {},
    storeMap: {},
    pincode: '',
    needToDisabledLocationHeader: false,
  },
  emailRegex: undefined,
};

export const checkoutSlice = createSlice({
  name: 'commerceCheckout',
  initialState,
  reducers: {
    changeStep: (state, action) => {
      state.currentStep = action.payload;
    },
    resetCheckout: () => initialState,
    resetPickupStoreOptions: (state, action) => {
      state.fulfillmentTypesAndStoreMap = resetThirdPartyStoreList(state.fulfillmentTypesAndStoreMap, action.payload);
    },
    setPickupDrawerVisibility: (state, action) => {
      state.pickupDrawerVisibility = action.payload;
    },
    setPickupContactDrawerVisibility: (state, action) => {
      state.pickupContactDrawerVisibility = action.payload;
    },
    setCheckoutErrorState: (state, action) => {
      state.hasError = action.payload;
    },
    setPlaceOrderDisabled: (state, action) => {
      const placeOrderEnablerStack = [...state.placeOrderEnablerStack];
      if (action.payload) {
        placeOrderEnablerStack.push(action.payload);
      } else {
        placeOrderEnablerStack.length && placeOrderEnablerStack.pop();
      }
      state.placeOrderEnablerStack = placeOrderEnablerStack;
      state.disabledPlaceOrder = placeOrderEnablerStack?.length ? true : false;
    },
    setDeliveryOptionsVisibility: (state, action) => {
      state.deliveryOptionsVisibility = action.payload;
    },
    setNotificationPopupVisibility: (state, action) => {
      state.notificationPopupVisibility = action.payload;
    },
    setEventsModalVisibility: (state, action) => {
      state.eventsModalVisibility = action.payload;
    },
    setDeliveryOptionsContainerState: (state, action) => {
      state.deliveryOptionsContainerState = action.payload;
    },
    setShippingDetailsContainerState: (state, action) => {
      state.shippingDetailsContainerState = action.payload;
    },
    setTaxContactContainerState: (state, action) => {
      state.taxContactContainerState = action.payload;
    },
    setEmailRegex: (state, action) => {
      state.emailRegex = action.payload;
    },
    setPlaceOrderButtonLoading: (state, action) => {
      state.loadingState = {
        ...state.loadingState,
        placeOrder: {
          isLoading: action.payload,
          hasError: false,
        },
      };
    },
    setDeliveryOptionConfirmButtonLoading: (state, action) => {
      state.loadingState = {
        ...state.loadingState,
        deliveryOption: {
          isLoading: action.payload,
          hasError: false,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state: InitialCheckoutState) => {
      state.isLoading = true;
    });

    /**
     * Create checkout details
     */
    builder.addCase(createCheckoutSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.currentStep = CheckoutStep.DeliveryAddress;
      state.checkout = action.payload as CheckoutResponse;
    });
    /**
     * Update checkout address details
     */
    builder.addCase(updateCheckoutSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.checkout = action.payload as CheckoutResponse;
    });
    /**
     * Get checkout details
     */
    builder.addCase(getCheckoutSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.checkout = action.payload as CheckoutResponse;
    });
    /**
     * place order
     */
    builder.addCase(placeOrderCheckoutSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.checkout = action.payload as CheckoutResponse;
    });

    /**
     * create payment method in checkout
     */
    builder.addCase(createPaymentCheckoutSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.checkout = action.payload as CheckoutResponse;
      getCountrySpecificMasterConfig()?.userCheckoutProfileEnable &&
        setCheckoutPaymentProfile(action.payload as CheckoutResponse);
    });

    /**
     * update payment method in checkout
     */
    builder.addCase(updatePaymentCheckoutSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.checkout = action.payload as CheckoutResponse;
      getCountrySpecificMasterConfig()?.userCheckoutProfileEnable &&
        setCheckoutPaymentProfile(action.payload as CheckoutResponse);
    });

    /**
     * confirm delivery option method in checkout
     */
    builder.addCase(confirmDeliveryOptionCheckoutSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.checkout = action.payload as CheckoutResponse;
      getCountrySpecificMasterConfig()?.userCheckoutProfileEnable &&
        setCheckoutProfile(action.payload as CheckoutResponse);
    });

    /**
     * update delivery option method in checkout
     */
    builder.addCase(updateDeliveryOptionCheckoutSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.checkout = action.payload as CheckoutResponse;
      getCountrySpecificMasterConfig()?.userCheckoutProfileEnable &&
        setCheckoutProfile(action.payload as CheckoutResponse);
    });

    /**
     * get fulfillment types
     */

    builder.addCase(getFulfillmentTypesSliceReq.fulfilled, (state, action) => {
      state.fulfillmentTypesAndStoreMap = transformFulfillmentTypesData(action.payload as FulfillmentTypeCollection);
      state.loadingState = {
        ...state.loadingState,
        [getRequestName(action.type)]: {
          isLoading: false,
          hasError: false,
        },
      };
    });

    builder.addCase(getFulfillmentTypesSliceReq.pending, (state, action) => {
      state.loadingState = {
        ...state.loadingState,
        [getRequestName(action.type)]: {
          isLoading: true,
          hasError: false,
        },
      };
    });

    builder.addCase(getFulfillmentTypesSliceReq.rejected, (state, action) => {
      state.loadingState = {
        ...state.loadingState,
        [getRequestName(action.type)]: {
          isLoading: false,
          hasError: true,
        },
      };
    });
    /**
     * get store Locations
     */

    builder.addCase(getStoreLocationsSliceReq.fulfilled, (state, action) => {
      state.loadingState = {
        ...state.loadingState,
        [getRequestName(action.type)]: {
          isLoading: false,
          hasError: false,
        },
      };
      state.fulfillmentTypesAndStoreMap = transformFulfillmentLocations(
        action.payload as CartPickupLocationResponse,
        current(state.fulfillmentTypesAndStoreMap),
        action.meta.arg.pincode,
        action.meta.arg.pagination,
      );
    });

    builder.addCase(getStoreLocationsSliceReq.pending, (state, action) => {
      state.loadingState = {
        ...state.loadingState,
        [getRequestName(action.type)]: {
          isLoading: true,
          hasError: false,
        },
      };
    });

    builder.addCase(getStoreLocationsSliceReq.rejected, (state, action) => {
      state.loadingState = {
        ...state.loadingState,
        [getRequestName(action.type)]: {
          isLoading: false,
          hasError: true,
        },
      };
      state.fulfillmentTypesAndStoreMap.storeMap = getSelectedFulfillmentLocationStoreMap(
        current(state.fulfillmentTypesAndStoreMap.storeMap),
        action.meta.arg.payload?.allocations?.[0]?.fulfillmentTypeId || '',
      );
    });

    /**
     * get fulfillment cms content
     */

    builder.addCase(getFulfillmentCmsDataSliceReq.fulfilled, (state, action) => {
      state.fulfillmentCmsContent = transformFulfillmentCmsData(action.payload as IFulfillmentCmsResponse);
    });

    /**
     * get fulfillment cms content
     */

    builder.addCase(getTncCmsDataSliceReq.fulfilled, (state, action) => {
      state.cmsContent = transformCheckoutCmsData(action.payload as ICheckoutCmsResponse);
    });

    /**
     * get checkout by order id
     */
    builder.addCase(getCheckoutByOrderIdSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.checkout = action.payload as CheckoutResponse;
    });

    /**
     * get tax documents by checkout id
     */
    builder.addCase(getTaxDocumentsSliceReq.fulfilled, (state, action) => {
      state.isLoading = false;
      state.hasError = false;
      const taxDocumentsResponse = action.payload as TaxDocumentResponse;
      state.taxDocuments = taxDocumentsResponse?.taxDocuments;
    });

    /**
     * Get cart details
     * // TODO: To be used in guest flow
     */
    // builder.addCase(getCheckoutCartSliceReq.fulfilled, (state: InitialCheckoutState, action) => {
    //   state.isLoading = false;
    //   state.hasError = false;
    //   state.cart = action.payload as Cart;
    // });

    /**
     * Common reducers
     */
    builder.addMatcher(isRejectedAction, (state: InitialCheckoutState, action) => {
      if (getRequestName(action.type) != checkoutApiGenerics.PAYMENT_METHOD_LOADING) {
        state.hasError = true;
      }
      state.error = action.error;
      state.isLoading = false;
      state.loadingState = {
        ...state.loadingState,
        [getRequestName(action.type)]: {
          isLoading: false,
          hasError: true,
          error: action.error,
        },
      };
    });
    builder.addMatcher(isFulfilledAction, (state: InitialCheckoutState, action) => {
      state.isLoading = false;
      state.hasError = false;
      state.loadingState = {
        ...state.loadingState,
        [getRequestName(action.type)]: {
          isLoading: false,
          hasError: false,
        },
      };
    });
    builder.addMatcher(isPendingGenericAction, (state: InitialCheckoutState, action) => {
      state.isLoading = true;
      state.hasError = false;
      if (!state?.loadingState?.deliveryOption?.isLoading) {
        state.loadingState = {
          ...state.loadingState,
          [getRequestName(action.type)]: {
            isLoading: true,
            hasError: false,
          },
        };
      }
    });
  },
});

export const {
  changeStep,
  resetCheckout,
  setPickupDrawerVisibility,
  setPickupContactDrawerVisibility,
  setCheckoutErrorState,
  setPlaceOrderDisabled,
  resetPickupStoreOptions,
  setDeliveryOptionsVisibility,
  setDeliveryOptionsContainerState,
  setShippingDetailsContainerState,
  setTaxContactContainerState,
  setPlaceOrderButtonLoading,
  setDeliveryOptionConfirmButtonLoading,
  setNotificationPopupVisibility,
  setEventsModalVisibility,
  setEmailRegex,
} = checkoutSlice.actions;
